import { useState, useEffect, useCallback } from 'react';
import { proveedoresService } from '../../services';

/**
 * Hook para manejar un proveedor individual (crear/editar)
 */
export const useProveedor = (proveedorId = null) => {
  const [proveedor, setProveedor] = useState({
    // Información básica REQUERIDA
    name: '',
    address: '',
    unit: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    phone: '',

    // Información opcional
    website: '',
    contact_name: '',
    phone_secondary: '',
    email: '',
    phones: [],
    
    // Términos de pago
    payment_terms: 'COD', // COD o TERMS
    credit_days: 0,
    credit_limit: 0,
    payment_method: 'CHECK', // CHECK, CARD, ZELLE, BANK_TRANSFER, OTHER
    
    // Información bancaria
    bank_name: '',
    account_number: '',
    routing_number: '',
    swift_code: '',
    
    // Estado y control
    status: 'ACTIVE', // ACTIVE, INACTIVE, BLOCKED, PENDING
    is_active: true,
    
    // Notas
    notes: ''
  });
  
  const [originalProveedor, setOriginalProveedor] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const isEditing = Boolean(proveedorId);

  /**
   * Cargar datos del proveedor (solo en modo edición)
   */
  const loadProveedor = useCallback(async () => {
    if (!proveedorId) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await proveedoresService.getProveedor(proveedorId);
      
      if (result.success) {
        // Transformar los datos de la API al formato esperado por el formulario
        const proveedorData = {
          id: result.data.id,
          // Información básica
          name: result.data.name || '',
          address: result.data.address || '',
          unit: result.data.unit || '',
          city: result.data.city || '',
          state: result.data.state || '',
          postal_code: result.data.postal_code || '',
          country: result.data.country || '',
          website: result.data.website || '',
          
          // Información de contacto
          contact_name: result.data.contact_name || '',
          phone: result.data.phone || '',
          phone_secondary: result.data.phone_secondary || '',
          phones: result.data.phones || [],
          email: result.data.email || '',
          
          // Términos de pago
          payment_terms: result.data.payment_terms || 'COD',
          credit_days: result.data.credit_days || 0,
          credit_limit: result.data.credit_limit || 0,
          payment_method: result.data.payment_method || 'CHECK',
          
          // Información bancaria
          bank_name: result.data.bank_name || '',
          account_number: result.data.account_number || '',
          routing_number: result.data.routing_number || '',
          swift_code: result.data.swift_code || '',
          
          // Estado y control
          status: result.data.status || 'ACTIVE',
          is_active: result.data.is_active !== false,
          
          // Notas
          notes: result.data.notes || ''
        };

        setProveedor(proveedorData);
        setOriginalProveedor({ ...proveedorData });
      } else {
        setError(result.message || 'Error al cargar los datos del proveedor');
      }
    } catch (err) {
      setError('Error de conexión al cargar los datos del proveedor');
      console.error('Error loading proveedor:', err);
    } finally {
      setIsLoading(false);
    }
  }, [proveedorId]);

  /**
   * Actualizar datos del proveedor
   */
  const updateProveedorData = useCallback((field, value) => {
    setProveedor(prev => {
      const updated = { ...prev, [field]: value };
      
      // Verificar si hay cambios
      const hasChanges = JSON.stringify(updated) !== JSON.stringify(originalProveedor);
      setHasChanges(hasChanges);
      
      return updated;
    });
  }, [originalProveedor]);

  /**
   * Guardar proveedor (crear o actualizar)
   */
  const saveProveedor = useCallback(async () => {
    try {
      // Preparar datos para enviar a la API según el schema del backend
      // Extraer el teléfono principal del array phones si phone está vacío
      const primaryPhone = proveedor.phone ||
        (proveedor.phones && proveedor.phones.length > 0
          ? (proveedor.phones.find(p => p.is_primary)?.number || proveedor.phones[0]?.number || '')
          : '');

      const proveedorData = {
        // Información básica REQUERIDA
        name: proveedor.name,
        unit: proveedor.unit || null,
        address: proveedor.address,
        city: proveedor.city || null,
        state: proveedor.state || null,
        postal_code: proveedor.postal_code || null,
        country: proveedor.country,
        phone: primaryPhone,

        // Información opcional
        phones: proveedor.phones && proveedor.phones.length > 0 ? proveedor.phones : null,
        website: proveedor.website || null,
        contact_name: proveedor.contact_name || null,
        phone_secondary: proveedor.phone_secondary || null,
        email: proveedor.email || null,
        
        // Términos de pago
        payment_terms: proveedor.payment_terms || 'COD',
        credit_days: proveedor.credit_days || null,
        credit_limit: proveedor.credit_limit || null,
        payment_method: proveedor.payment_method || 'CHECK',
        
        // Información bancaria
        bank_name: proveedor.bank_name || null,
        account_number: proveedor.account_number || null,
        routing_number: proveedor.routing_number || null,
        swift_code: proveedor.swift_code || null,
        
        // Estado y control
        status: proveedor.status || 'ACTIVE',
        is_active: proveedor.is_active !== false,
        
        // Notas
        notes: proveedor.notes || null
      };
      
      // Limpiar campos undefined donde no se permiten
      Object.keys(proveedorData).forEach(key => {
        if (proveedorData[key] === undefined) {
          delete proveedorData[key];
        }
      });
      
      console.log('Datos a enviar a la API:', proveedorData);
      
      let result;
      
      if (isEditing) {
        // Actualizar proveedor existente
        result = await proveedoresService.updateProveedor(proveedorId, proveedorData);
      } else {
        // Crear nuevo proveedor
        result = await proveedoresService.createProveedor(proveedorData);
      }
      
      if (result.success) {
        return {
          success: true,
          message: result.message || `Proveedor ${proveedor.name} ${isEditing ? 'actualizado' : 'creado'} exitosamente`,
          data: result
        };
      } else {
        // Manejar errores de validación
        return {
          success: false,
          error: result.message || 'Error al guardar el proveedor',
          validation_errors: result.validation_errors || [],
          validationErrors: result.validationErrors || {}
        };
      }
    } catch (err) {
      console.error('Error saving proveedor:', err);
      return {
        success: false,
        error: 'Error de conexión al guardar el proveedor'
      };
    }
  }, [proveedor, isEditing, proveedorId]);

  /**
   * Resetear formulario
   */
  const resetProveedor = useCallback(() => {
    if (isEditing) {
      setProveedor({ ...originalProveedor });
    } else {
      setProveedor({
        unit: '',
        // Información básica
        name: '',
        address: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        phone: '',

        // Información opcional
        phones: [],
        website: '',
        contact_name: '',
        phone_secondary: '',
        email: '',

        // Términos de pago
        payment_terms: 'COD',
        credit_days: 0,
        credit_limit: 0,
        payment_method: 'CHECK',

        // Información bancaria
        bank_name: '',
        account_number: '',
        routing_number: '',
        swift_code: '',

        // Estado y control
        status: 'ACTIVE',
        is_active: true,

        // Notas
        notes: ''
      });
    }
    setHasChanges(false);
  }, [isEditing, originalProveedor]);

  /**
   * Limpiar errores
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Cargar proveedor al montar (solo en modo edición)
  useEffect(() => {
    if (isEditing) {
      loadProveedor();
    }
  }, [isEditing, loadProveedor]);

  return {
    proveedor,
    isEditing,
    isLoading,
    error,
    hasChanges,
    updateProveedorData,
    saveProveedor,
    resetProveedor,
    clearError
  };
};